import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, inject, InjectFlags, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DataApiResponse, Option, ProfessionAliasModel } from '@digilize/shared/definitions/src';
import { SingleSelectSearchComponent } from '@digilize/shared/ui/components/src/lib/form-items/single-select-search/single-select-search.component';
import { forkJoin, Observable, throwError } from 'rxjs';

import { createOptions } from '../../../helpers/helpers';
import { ProfessionsService } from '../../../services/professions.service';
import { WizardService } from '../../../services/wizard.service';

@Component({
  selector: 'bwc-desired-occupations',
  templateUrl: './desired-occupations.component.html',
  styleUrls: ['./desired-occupations.component.scss'],
})
export class DesiredOccupationsComponent implements OnInit {
  @Input() prevBut: boolean;
  @Input() closeBut: boolean;
  @ViewChild('searchAutocomplete') searchAutocomplete: SingleSelectSearchComponent;
  @Output() emitClose = new EventEmitter();

  wizardService = inject(WizardService, InjectFlags.Optional);

  form: FormGroup;
  jobsList: Option[] = [];
  dataLoaded = false;
  loadedJobsFullList = [];
  searchValue = '';
  initializedIndices = new Set<number>();
  workProfessionIds = new Set<string>();

  constructor(private professionsService: ProfessionsService, private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.createForm();
    forkJoin({
      list: this.professionsService.getListOfProfessions(this.searchValue),
      work: this.professionsService.getProfessionsMy(),
    }).subscribe(({ list, work }) => {
      this.loadedJobsFullList = list.data;
      this.jobsList = createOptions(list.data);
      this.workProfessionIds = new Set((work.data ?? []).map((p) => p.position_id).filter(Boolean));
      this.getDreamProfessions();
    });
  }

  createForm(): FormGroup {
    return this.fb.group({
      selectedJobs: this.fb.array([]),
    });
  }

  getDreamProfessions() {
    this.professionsService.getDreamProfessionsMy().subscribe((data) => {
      [...(data.data ?? [])]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .forEach((item) => {
          this.addToForm({
            id: item.id,
            name: item.name,
            position_id: item.position_id,
            order: item.order,
          });
        });
      this.dataLoaded = true;
    });
  }

  get selectedJobs() {
    return this.form.get('selectedJobs') as FormArray;
  }

  get filteredJobsList(): Option[] {
    const selectedIds = new Set([
      ...this.workProfessionIds,
      ...this.selectedJobs.controls
        .map((c) => c.get('position_id')?.value)
        .filter(Boolean),
    ]);
    return this.jobsList.filter((opt) => !selectedIds.has(opt.id));
  }

  getFilteredJobsListForIndex(i: number): Option[] {
    const selectedIds = new Set([
      ...this.workProfessionIds,
      ...this.selectedJobs.controls
        .filter((_, idx) => idx !== i)
        .map((c) => c.get('position_id')?.value)
        .filter(Boolean),
    ]);
    return this.jobsList.filter((opt) => !selectedIds.has(opt.id));
  }

  addToForm(item: DreamProfession) {
    const group = this.fb.group({
      id: [item.id],
      name: [item.name],
      position_id: [item.position_id],
      order: [item.order],
    });
    this.selectedJobs.push(group);
    this.resetOrderProperty();
  }

  addEmptyOccupation() {
    this.searchValue = '';
    this.addToForm({
      id: null,
      name: '',
      position_id: null,
      order: this.selectedJobs.length,
    });
  }

  handleFilteredOptionClick(option: Option) {
    this.addToForm({
      id: null,
      name: option.text,
      position_id: option.id,
      order: this.selectedJobs.length,
    });
    this.resetMainSearch();
  }

  private resetMainSearch() {
    this.searchValue = '';
    this.professionsService.getListOfProfessions('').subscribe((response) => {
      const combinedArray = this.loadedJobsFullList.concat(
        response.data.filter((newObj) => !this.loadedJobsFullList.some((srcObj) => srcObj.id === newObj.id))
      );
      this.loadedJobsFullList = [...combinedArray];
      this.jobsList = createOptions(response.data);
    });
  }

  setOccupationFromOption(option: Option, index: number) {
    const group = this.selectedJobs.at(index);
    group.patchValue({
      name: option.text,
      position_id: option.id,
    });
  }

  getFormControl(index: number, controlName: string): FormControl {
    const group = this.selectedJobs.at(index) as FormGroup;
    return group.get(controlName) as FormControl;
  }

  handleMainSearchValueChanged(event: string) {
    this.searchValue = event;
    this.professionsService.getListOfProfessions(event).subscribe((response) => {
      const combinedArray = this.loadedJobsFullList.concat(
        response.data.filter((newObj) => !this.loadedJobsFullList.some((srcObj) => srcObj.id === newObj.id))
      );
      this.loadedJobsFullList = [...combinedArray];
      this.jobsList = createOptions(response.data);
    });
  }

  handleValueChangedSearch(event: string, index: number) {
    if (!this.initializedIndices.has(index)) {
      this.initializedIndices.add(index);
      const item = this.selectedJobs.at(index);
      if (item.get('position_id').value) {
        return;
      }
    }

    const item = this.selectedJobs.at(index);
    const currentPositionId = item.get('position_id').value;
    if (currentPositionId) {
      const profession = this.loadedJobsFullList.find((p) => p.id === currentPositionId);
      if (profession && profession.name === event) {
        return;
      }
      item.patchValue({ position_id: null });
    }

    this.searchValue = event;
    this.professionsService.getListOfProfessions(event).subscribe((response) => {
      const combinedArray = this.loadedJobsFullList.concat(
        response.data.filter((newObj) => !this.loadedJobsFullList.some((srcObj) => srcObj.id === newObj.id))
      );
      this.loadedJobsFullList = [...combinedArray];
      this.jobsList = createOptions(response.data);
    });
  }

  handleScrolledToEndSearch(isVisible: boolean, _index: number) {
    const params = {
      'search.value': this.searchValue,
      start: this.jobsList.length || 0,
    };

    this.getListOfProfessions(params).subscribe((data) => {
      const newOptions = createOptions(data.data);
      this.jobsList = [...this.jobsList, ...newOptions];

      const combinedArray = this.loadedJobsFullList.concat(
        data.data.filter((newObj: any) => !this.loadedJobsFullList.some((srcObj) => srcObj.id === newObj.id))
      );
      this.loadedJobsFullList = [...combinedArray];
    });
  }

  private getListOfProfessions(
    overrideProps: { [key: string]: string | number } = {}
  ): Observable<DataApiResponse<ProfessionAliasModel[]>> {
    return this.professionsService.getListOfProfessionsParams({
      'search.value': '',
      length: 10,
      ...overrideProps,
    });
  }

  removeItem(index: number) {
    this.selectedJobs.removeAt(index);
    this.resetOrderProperty();
  }

  changePositionInList(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.selectedJobs.controls, event.previousIndex, event.currentIndex);
      this.resetOrderProperty();
      const updated = this.selectedJobs.controls.map((c) => c.value);
      this.selectedJobs.setValue(updated);
    }
  }

  goPrev() {
    this.saveData(false, true);
  }

  nextStep() {
    this.saveData();
  }

  saveData(close?: boolean, ifBack?: boolean) {
    const items = this.selectedJobs.controls.map((c) => c.value).filter((item) => item.position_id || item.name);

    const requestBody = { items };

    this.professionsService.putDreamProfessionsMy(requestBody).subscribe(
      () => {
        if (ifBack) {
          this.wizardService?.goToPrevious();
        } else if (close) {
          this.emitClose.emit('desired_occupations');
        } else {
          this.wizardService?.goToNext();
        }
      },
      (error) => {
        if (ifBack) {
          this.wizardService?.goToPrevious();
        }
        throwError('DreamProfessionsMy error:');
        console.error('DreamProfessionsMy error:', error);
      }
    );
  }

  resetOrderProperty() {
    setTimeout(() => {
      for (let i = 0; i < this.selectedJobs.controls.length; i++) {
        this.selectedJobs.controls[i].value.order = i;
      }
    }, 0);
  }

  close() {
    this.emitClose.emit('desired_occupations');
  }
}

interface DreamProfession {
  id?: string;
  name?: string;
  position_id?: string;
  order?: number;
}
